/** The languages of the interface. English is the source; the others are translations (docs/i18n/README.md). */
export interface Locale {
  /** The BCP 47 tag the page carries in `<html lang>`. */
  readonly code: string;
  /** The language's name in itself, as the picker shows it. */
  readonly name: string;
  readonly dir: 'ltr' | 'rtl';
  /** The tag for `Intl`: Latin digits for Arabic and Hindi so that codes, times and sizes read the same everywhere. */
  readonly intl: string;
}

export const LOCALES: readonly Locale[] = [
  { code: 'en', name: 'English', dir: 'ltr', intl: 'en' },
  { code: 'zh-Hans', name: '简体中文', dir: 'ltr', intl: 'zh-Hans' },
  { code: 'ja', name: '日本語', dir: 'ltr', intl: 'ja' },
  { code: 'ko', name: '한국어', dir: 'ltr', intl: 'ko' },
  { code: 'fr', name: 'Français', dir: 'ltr', intl: 'fr' },
  { code: 'es', name: 'Español', dir: 'ltr', intl: 'es' },
  { code: 'hi', name: 'हिन्दी', dir: 'ltr', intl: 'hi-u-nu-latn' },
  { code: 'ar', name: 'العربية', dir: 'rtl', intl: 'ar-u-nu-latn' },
  { code: 'pt-BR', name: 'Português (Brasil)', dir: 'ltr', intl: 'pt-BR' },
  { code: 'de', name: 'Deutsch', dir: 'ltr', intl: 'de' },
];

export const DEFAULT_LOCALE = 'en';

export function findLocale(code: string): Locale | undefined {
  return LOCALES.find((locale) => locale.code === code);
}

/** The best of our languages for the browser's list of preferences (`navigator.languages`), or English. */
export function matchLocale(preferred: readonly string[]): string {
  for (const tag of preferred) {
    const lower = tag.toLowerCase();
    const [language = ''] = lower.split('-');
    if (language === 'zh') {
      // Traditional Chinese is not offered; Simplified is the closest we have
      return 'zh-Hans';
    }
    if (language === 'pt') return 'pt-BR';
    const hit = LOCALES.find((locale) => locale.code.toLowerCase() === language);
    if (hit) return hit.code;
  }
  return DEFAULT_LOCALE;
}
